// The lux-meter guide: an illustrated, animated walk-through that stops at the reading
// until the number is typed in. One reading per measurement: before each home session
// (full the first time on a monitor, short afterwards), and by staff on the Calibrate page.
//
// The server judges the reading (brightness.py): it must be plausible for a white screen,
// and a check must be within the tolerance of the monitor's baseline. A patient never sees
// the number aimed for, only whether to turn the brightness up or down.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { LuxVerdict } from "../api";
import { errorText } from "../hooks";
import { SceneDone, SceneNeed, ScenePower, ScenePress, SceneRotate, SceneSettings, SceneType, SceneWarm } from "./LuxScenes";
import { WhiteScreen } from "./WhiteScreen";

export type GuideVariant = "full" | "short";
export interface GuideNotes { warmed_up: boolean; settings_unchanged: boolean; variant: GuideVariant }

interface Props {
  variant: GuideVariant;
  /** The server's verdict on the reading (sent as a one-item list). */
  judge: (readings: number[]) => Promise<LuxVerdict>;
  /** Called once with the reading the server accepted. */
  onDone: (readings: number[], verdict: LuxVerdict, notes: GuideNotes) => void;
  /** Monitor settings to use, e.g. "Brightness 40, Standard mode, HDR off". */
  instructions?: string;
  title?: string;
  /** Called when the person asks for the full guide from the short one. */
  onWantFull?: () => void;
}

type Kind = "info" | "confirm" | "measure" | "result";
interface Step { id: string; kind: Kind; title: string; say: string; scene: ReactNode; list?: string[] }

const VOICE_KEY = "vtlite.guide.voice";

function speak(on: boolean, text: string) {
  if (!on || typeof window === "undefined" || !("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 0.92;
  window.speechSynthesis.speak(u);
}

export function LuxGuide(p: Props) {
  const [i, setI] = useState(0);
  const [value, setValue] = useState("");
  const [shown, setShown] = useState(false);      // the white screen was shown for this reading
  const [white, setWhite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [verdict, setVerdict] = useState<LuxVerdict | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warm, setWarm] = useState(false);
  const [same, setSame] = useState(false);
  const [ready, setReady] = useState<Record<string, boolean>>({});
  const [replay, setReplay] = useState(0);
  const [voice, setVoice] = useState(() => { try { return localStorage.getItem(VOICE_KEY) === "1"; } catch { return false; } });
  const input = useRef<HTMLInputElement>(null);
  const finished = useRef(false);

  const steps = useMemo<Step[]>(() => {
    const out: Step[] = [];
    if (p.variant === "full") {
      out.push({ id: "need", kind: "info", title: "Measure your screen’s brightness", scene: <SceneNeed />,
        say: "This takes about two minutes. You need the light meter from your kit. We will show the screen white, and you will hold the meter against it and type the number it shows." });
      out.push({ id: "warm", kind: "confirm", title: "Let the monitor warm up", scene: <SceneWarm />,
        say: "A monitor gets brighter during its first minutes. Switch it on at least 15 minutes before you measure." });
      out.push({ id: "settings", kind: "info", title: "Keep everything the same", scene: <SceneSettings />,
        say: "Do not press the monitor’s buttons unless this guide asks you to. Keep the room lights as they usually are."
          + (p.instructions ? ` The monitor should be set to: ${p.instructions}.` : "") });
      out.push({ id: "power", kind: "info", title: "Switch the meter on", scene: <ScenePower />,
        say: "Press the red power button on the meter. The display shows a number and the word LUX. If it says FC instead, press the LUX/FC button once." });
      out.push({ id: "rotate", kind: "info", title: "Turn the white sensor", scene: <SceneRotate />,
        say: "Turn the round white sensor on top of the meter so it points away from the meter’s display. When the sensor touches the screen, the display will face you, so you can read it." });
    } else if (p.variant === "short") {
      out.push({ id: "ready", kind: "confirm", title: "Get the meter ready", scene: <SceneRotate />,
        say: "Before measuring, check each of these.",
        list: ["The monitor has been on for at least 15 minutes", "I have not changed the monitor’s buttons, and the room lights are as usual",
          "The meter is on and its display says LUX", "The white sensor points away from the meter’s display"] });
    }
    out.push({ id: "measure", kind: "measure", title: "Measure the white screen",
      scene: <ScenePress />,
      say: "Press Show the white screen. Press the white sensor flat against the middle of the circle and hold still until the number stops changing. You can press HOLD to keep the number. Remember it, then press any key to come back and type it." });
    out.push({ id: "result", kind: "result", title: "Result", scene: <SceneDone ok={!verdict || verdict.verdict === "ok"} />, say: "" });
    return out;
  }, [p.variant, p.instructions, verdict]);

  const step = steps[Math.min(i, steps.length - 1)] as Step;   // never empty: the result step is always there
  const measureStep = steps.findIndex((s) => s.kind === "measure");

  useEffect(() => {
    const text = step.kind === "result" ? (verdict?.message ?? "") : `${step.title}. ${step.say}${step.list ? " " + step.list.join(". ") : ""}`;
    speak(voice, text);
  }, [step.id, verdict, voice]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); }, []);
  useEffect(() => { if (shown && !white) input.current?.focus(); }, [shown, white]);

  const setVoiceOn = (on: boolean) => {
    setVoice(on);
    try { localStorage.setItem(VOICE_KEY, on ? "1" : "0"); } catch { /* private window */ }
    if (!on && "speechSynthesis" in window) window.speechSynthesis.cancel();
  };

  const go = (to: number) => { setI(Math.max(0, Math.min(to, steps.length - 1))); setValue(""); setShown(false); setError(null); };

  const submitReading = async () => {
    const v = Number(value.replace(",", "."));
    if (value.trim() === "" || !Number.isFinite(v) || v < 0) { setError("Type the number the meter showed (digits only)."); return; }
    setValue("");
    setShown(false);
    setError(null);
    setBusy(true);
    try {
      const r = await p.judge([v]);
      setVerdict(r);
      setI(steps.length - 1);
      if (r.verdict === "ok" && !finished.current) {
        finished.current = true;
        p.onDone([v], r, { warmed_up: warm || !!ready["0"], settings_unchanged: same || !!ready["1"], variant: p.variant });
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const again = (fromStart: boolean) => {
    // measure again (after adjusting the monitor, or a reading that made no sense)
    setVerdict(null);
    finished.current = false;
    go(fromStart && p.variant === "full" ? steps.findIndex((s) => s.id === "power") : measureStep);
  };

  const confirmOk = step.id === "warm" ? warm : step.id === "ready" ? [0, 1, 2, 3].every((k) => ready[String(k)]) : true;
  const isDone = step.kind === "result" && verdict?.verdict === "ok";

  return (
    <section className="lux-guide" aria-label={p.title ?? "Measure the screen brightness"}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <div className="lux-progress" aria-label={`Step ${i + 1} of ${steps.length}`}>
          {steps.map((s, k) => <span key={s.id} className={k < i ? "done" : k === i ? "now" : ""} />)}
          <span className="lux-count">step {i + 1} of {steps.length}</span>
        </div>
        <label className="lux-voice"><input type="checkbox" checked={voice} onChange={(e) => setVoiceOn(e.target.checked)} />Read the steps aloud</label>
      </div>
      {p.title && i === 0 && <div className="small muted">{p.title}</div>}
      <h2>{step.kind === "result" ? (isDone ? "Done" : verdict ? "Let’s try again" : "Checking…") : step.title}</h2>
      <div key={`${step.id}-${replay}`}>{step.scene}</div>

      {step.kind !== "result" && <p className="lux-say">{step.say}</p>}
      {step.list && (
        <div className="lux-checks">
          {step.list.map((t, k) => (
            <label key={t}><input type="checkbox" checked={!!ready[String(k)]}
              onChange={(e) => setReady({ ...ready, [String(k)]: e.target.checked })} />{t}</label>
          ))}
        </div>
      )}
      {step.id === "warm" && (
        <div className="lux-checks">
          <label><input type="checkbox" checked={warm} onChange={(e) => setWarm(e.target.checked)} />The monitor has been on for at least 15 minutes</label>
        </div>
      )}
      {step.id === "settings" && (
        <div className="lux-checks">
          <label><input type="checkbox" checked={same} onChange={(e) => setSame(e.target.checked)} />The monitor settings and room lights are as usual</label>
        </div>
      )}

      {step.kind === "measure" && (
        <>
          <button className="big" onClick={() => { setWhite(true); setShown(true); }}>
            {shown ? "Show the white screen again" : "Show the white screen"}</button>
          {shown && !white && (
            <div className="lux-entry">
              <label style={{ fontSize: "1.3rem" }}>The meter showed
                <input ref={input} inputMode="decimal" aria-label="Meter reading" value={value}
                  onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void submitReading(); }} />
              </label>
              <span style={{ fontSize: "1.3rem" }}>lux</span>
              <button className="big" disabled={busy} onClick={() => void submitReading()}>
                {busy ? "Checking…" : "Save and check"}</button>
            </div>
          )}
          {shown && !white && <div style={{ marginTop: "0.6rem" }}><SceneTypeHint /></div>}
        </>
      )}

      {step.kind === "result" && verdict && (
        <>
          <div className={`lux-msg ${isDone ? "ok" : "warn"}`} role="status">{verdict.message}</div>
          {!isDone && (
            <div className="lux-controls">
              {(verdict.verdict === "too_low" || verdict.verdict === "too_high") &&
                <button className="big" onClick={() => again(false)}>I pressed the button: measure again</button>}
              {(verdict.verdict === "implausible" || verdict.verdict === "missing" || verdict.verdict === "no_baseline") &&
                <button className="big" onClick={() => again(true)}>Measure again</button>}
            </div>
          )}
        </>
      )}
      {error && <p className="error" role="alert">{error}</p>}

      {step.kind !== "result" && (
        <div className="lux-controls">
          {i > 0 && <button className="secondary" onClick={() => go(i - 1)}>Back</button>}
          <button className="secondary" onClick={() => setReplay(replay + 1)}>Replay</button>
          {step.kind !== "measure" && <button className="big" disabled={!confirmOk} onClick={() => go(i + 1)}>Next</button>}
          {p.variant === "short" && p.onWantFull && i === 0 && <button className="link" onClick={p.onWantFull}>Show me the full guide</button>}
        </div>
      )}
      {white && <WhiteScreen onDone={() => setWhite(false)} />}
    </section>
  );
}

/** A small reminder next to the entry box: the meter's number goes here. */
function SceneTypeHint() {
  return <details className="small muted"><summary>Where do I find the number?</summary><SceneType /></details>;
}
